import React from 'react';
import { PlusCircleOutlined } from '@ant-design/icons';
import { Form, Input, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import SearchBoxList from '../../global/SearchBoxList';
import { ExportBuilderAdvancedSegmentField } from '../../../../types/export_builder_advanced';

type ColumnContentConfiguratorProps = {
  fields: ExportBuilderAdvancedSegmentField[];
};

const ColumnContentConfigurator: React.FC<ColumnContentConfiguratorProps> = ({ fields }) => {
  const { t } = useTranslation();

  const [popoverVisible, setPopoverVisible] = React.useState(false);
  const [cursorPosition, setCursorPosition] = React.useState<number>(0);
  const [inputFocused, setInputFocused] = React.useState<boolean>(false);
  const inputRef = React.useRef<any>(null);

  const { values, setFieldValue, setFieldTouched, touched, errors } = useFormikContext<any>();

  const previewExample = values.columnContent.replace(
    /([?{2]+)?\w+([?}2]+)/g,
    (match: string | any[]) => {
      const filterMatch = match.slice(2, match.length - 2);
      const option = fields.find(({ code }) => code === filterMatch);
      return option ? option.sample_value : `${match}`;
    }
  );

  return (
    <div>
      <Form.Item
        label={t('exportBuilderAdvanced:columnContent')}
        validateStatus={errors.columnContent && touched.columnContent ? 'error' : ''}
        help={
          (errors.columnContent && touched.columnContent && t('validation:required')) || undefined
        }
        required={!!values.segmentId}
      >
        <Input
          data-testid="column-content"
          ref={inputRef}
          value={values.columnContent}
          onChange={e => setFieldValue('columnContent', e.target.value)}
          onBlur={() => {
            setFieldTouched('columnContent');
            // wait to allow popover to access the focus state
            setTimeout(() => {
              setInputFocused(false);
            }, 200);
          }}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement;
            setCursorPosition(target.selectionStart ?? values.columnContent.length);
            setInputFocused(true);
          }}
          onSelect={(e: React.SyntheticEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement;
            setCursorPosition(target.selectionStart ?? 0);
          }}
          onClick={(e: React.MouseEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement;
            setCursorPosition(target.selectionStart ?? 0);
          }}
          onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement;
            setCursorPosition(target.selectionStart ?? 0);
          }}
          addonAfter={
            !!values.segmentId && (
              <Popover
                open={popoverVisible}
                onOpenChange={visible => {
                  if (visible && inputRef.current) {
                    const input = inputRef.current.input;
                    const position = inputFocused
                      ? input.selectionStart ?? values.columnContent.length
                      : values.columnContent.length;
                    setCursorPosition(position);
                  }
                  setPopoverVisible(visible);
                }}
                content={
                  <SearchBoxList
                    values={fields}
                    valueKey="code"
                    onClick={code => {
                      const currentValue = values.columnContent;
                      const beforeCursor = currentValue.slice(0, cursorPosition);
                      const afterCursor = currentValue.slice(cursorPosition);
                      const newValue = `${beforeCursor}{{${code}}}${afterCursor}`;
                      setFieldValue('columnContent', newValue);
                      setPopoverVisible(false);

                      // Restore focus and set cursor position after the inserted code
                      // timeout to wait for the input to update
                      setTimeout(() => {
                        if (inputRef.current) {
                          const input = inputRef.current.input;
                          input.focus();
                          const newCursorPos = cursorPosition + code.length + 4; // +4 for {{}}
                          input.setSelectionRange(newCursorPos, newCursorPos);
                        }
                      }, 0);
                    }}
                  />
                }
                trigger="click"
                placement="left"
                className="overflow-auto"
              >
                <span className="cursor-pointer" data-testid="column-content-add">
                  <span className="mr-1">{t('common:add')}</span>
                  <PlusCircleOutlined />
                </span>
              </Popover>
            )
          }
        />
      </Form.Item>

      <div>
        {t('exportBuilderAdvanced:configExample')}: <span className="italic">{previewExample}</span>
      </div>
    </div>
  );
};

export default ColumnContentConfigurator;
